import classnames from 'classnames';
import { Checkbox } from 'components/shared/Checkbox/Checkbox';
import { ProgressionBar } from 'components/shared/ProgressionBar/ProgressionBar';
import { RemainingTimeBadge } from 'components/shared/RemainingTimeBadge/RemainingTmeBadge';
import { WithErrorTooltip } from 'components/shared/WithErrorTooltip/WithErrorTooltip';
import { TradeWay } from 'models/TradeWay';
import { FC, useCallback, useMemo } from 'react';
import { isBatchFormActionFailed, isBatchFormFailedActionRetryable } from 'state/batchForm/infos/infos.helpers';
import { ExposedFormStatus } from 'state/forms/models';
import styled from 'styled-components';
import { dateNow } from 'utils/core/now';

import { FormDeleteIconButton } from './FormDeleteIconButton/FormDeleteIconButton';
import { connectedBatchFormHeaderCell } from './FormHeaderCell.connect';
import type { BatchFormHeaderCellProps } from './FormHeaderCell.models';

type AccessibleBatchFormHeaderCellProps = BatchFormHeaderCellProps & {
  ariaLabel?: string;
  role?: string;
  ariaColIndex?: number;
  ariaRowIndex?: number;
};

import { BatchFormOptionsMenu } from './FormOptionsMenu/FormOptionsMenu';
import { FormReloadIconButton } from './FormReloadIconButton/FormReloadIconButton';
import { FormStatusIconText } from './FormStatusIconText/FormStatusIconText';

// TODO: batch screen unit test
export const StyledDiv = styled.div`
  position: relative;
  font-weight: 500;

  & > .progression-bar-component {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
  }

  .with-error-tooltip-component {
    position: unset !important;

    & > .tooltip {
      position: absolute;
      left: 0.4rem;
      right: 0.4rem;
      transform: translateY(-0.7rem);
    }
  }
`;

export const BatchFormHeaderCellComponent: FC<AccessibleBatchFormHeaderCellProps> = ({
  className,
  e2e,
  workflowErrors,
  productSubtype,
  exposedFormStatus,
  formId,
  formJsonRepresentation,
  formStatus,
  isSelected,
  isLoading,
  onCheckboxChanged,
  onFailedFormDeleted,
  onFailedFormRetried,
  onPricingTimeout,
  onQuoteExpired,
  pricingEstimatedDurationInMilliseconds,
  tradeAuthStartTimeInMillis,
  tradeAuthDurationInMillis,
  pricingStartDate,
  quoteSolveResult,
  tradeWay,
  warnings,
  tabId,
  isInternal,
  quoteCreatedAt,
  ariaLabel,
  role = 'columnheader',
  ariaColIndex,
  ariaRowIndex,
}) => {
  // TODO Migrate to selector
  const isFormPricing = useMemo(() => formStatus === 'QUOTING', [formStatus]);
  const isFormQuoted = useMemo(() => formStatus === 'QUOTED', [formStatus]);
  const isFormRejected = useMemo(() => formStatus === 'REJECTED', [formStatus]);
  const isFormDuplicating = useMemo(() => formStatus === 'DUPLICATING', [formStatus]);
  const isFormActionFailed = useMemo(() => formStatus && isBatchFormActionFailed(formStatus), [formStatus]);
  const isFormQuotedWithWarnings = useMemo(
    () => formStatus === 'QUOTED' && exposedFormStatus === 'Warning',
    [formStatus, exposedFormStatus],
  );
  const canRetryAction = useMemo(() => formStatus && isBatchFormFailedActionRetryable(formStatus), [formStatus]);

  const shouldDisplayErrorWarningTooltip = useMemo(
    () => isFormRejected || (isInternal && isFormQuotedWithWarnings),
    [isFormRejected, isFormQuotedWithWarnings, isInternal],
  );

  const shouldDisplayProgressBar = isFormPricing || isFormQuoted;

  const divClassNames = useMemo(
    () =>
      classnames(
        'batch-grid-cell px-1',
        getHeaderColorClassFromStatus(exposedFormStatus, tradeWay),
        {
          'is-selected': isSelected,
          'with-border-bottom-thick': !shouldDisplayProgressBar,
        },
        className,
      ),
    [exposedFormStatus, isSelected, tradeWay, className, shouldDisplayProgressBar],
  );
  const staticIconText = useMemo(
    () => (
      <FormStatusIconText status={exposedFormStatus} quoteCreatedAt={quoteCreatedAt} quoteResult={quoteSolveResult} />
    ),
    [exposedFormStatus, quoteCreatedAt, quoteSolveResult],
  );
  const errorTitle = useMemo(
    () => (isInternal ? 'batchPricer.error.quoteRejected' : 'errorDetails.standardMessage'),
    [isInternal],
  );
  const onToggleCheckbox = useCallback(() => {
    onCheckboxChanged(!isSelected);
  }, [onCheckboxChanged, isSelected]);

  return (
    <StyledDiv
      className={classnames(divClassNames, 'h-100')}
      data-e2e={e2e}
      role={role}
      aria-label={ariaLabel}
      aria-colindex={ariaColIndex}
      aria-rowindex={ariaRowIndex}
    >
      {shouldDisplayErrorWarningTooltip ? (
        <div className="pt-2">
          <WithErrorTooltip
            canCopy
            contentToCopy={formJsonRepresentation}
            errors={workflowErrors}
            position="bottom"
            productSubtype={productSubtype}
            errorTitle={errorTitle}
            warnings={isInternal ? warnings : undefined}
          >
            <div className="d-flex align-items-center justify-content-between h-100 px-2 pe-0">
              <div className="flex-grow-1">
                <span onClick={onToggleCheckbox}>{staticIconText}</span>
              </div>
              <Checkbox
                e2e={e2e ? `${e2e}-select-checkbox` : undefined}
                disabled={isFormDuplicating}
                onChange={onCheckboxChanged}
                checked={isSelected}
                id={formId}
              />
              <BatchFormOptionsMenu className="me-1" formId={formId} tabId={tabId} />{' '}
            </div>
          </WithErrorTooltip>
        </div>
      ) : (
        <>
          <div className="d-flex align-items-center justify-content-between h-100 px-2 pe-0">
            <div className="flex-grow-1" onClick={onToggleCheckbox}>
              {staticIconText}
            </div>
            {isFormActionFailed && (
              <div className="align-items-center d-flex" data-e2e="batch-form-header-cell-action-failed-actions">
                {canRetryAction && (
                  <FormReloadIconButton className="me-1" onClick={() => onFailedFormRetried(formStatus!)} />
                )}
                <FormDeleteIconButton className="me-1" onClick={onFailedFormDeleted} />
              </div>
            )}
            {isFormQuoted && (
              <RemainingTimeBadge
                className="p-2 mx-2"
                createAtMilliseconds={tradeAuthStartTimeInMillis}
                windowInMilliseconds={tradeAuthDurationInMillis}
              />
            )}
            {!isLoading && (
              <Checkbox
                e2e={e2e ? `${e2e}-select-checkbox` : undefined}
                disabled={isFormDuplicating}
                onChange={onCheckboxChanged}
                checked={isSelected}
                id={formId}
              />
            )}
            <BatchFormOptionsMenu className="me-1" formId={formId} tabId={tabId} />
          </div>
        </>
      )}

      {isFormPricing && (
        <ProgressionBar
          className="bg-info"
          durationInMilliseconds={pricingEstimatedDurationInMilliseconds ?? Infinity}
          startDate={pricingStartDate ?? new Date(dateNow())}
          onTimeout={onPricingTimeout}
        />
      )}
      {isFormQuoted && (
        <ProgressionBar
          className="bg-secondary"
          startFromRight
          durationInMilliseconds={tradeAuthDurationInMillis ?? Infinity}
          startDate={new Date(tradeAuthStartTimeInMillis ?? dateNow())}
          onTimeout={onQuoteExpired}
        />
      )}
    </StyledDiv>
  );
};

export const BatchFormHeaderCell = connectedBatchFormHeaderCell(BatchFormHeaderCellComponent);

function getHeaderColorClassFromStatus(status: ExposedFormStatus | undefined, tradeWay?: TradeWay): string {
  switch (status) {
    case 'NotReady':
    case 'Patching':
    case 'Validating':
      return 'bg-lvl1';
    case 'Ready':
      return 'bg-info bg-opacity-05';
    case 'InProgress':
      return 'bg-secondary bg-opacity-05';
    case 'Success':
      return `sg-price ${tradeWay?.toLowerCase()}`;
    case 'Expired':
      return 'bg-warning text-warning bg-opacity-10';
    case 'UnknownError':
      return 'bg-danger bg-opacity-05';
    case 'Rejected':
      return 'bg-danger bg-opacity-05';
    case 'ActionFailed':
    case 'Warning':
      return 'bg-warning bg-opacity-10';
    default:
      return 'bg-lvl2';
  }
}
